import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { PushPipe } from '@ngrx/component';
import { map, merge } from 'rxjs';

import type { HelpTranslationLinkMapKey } from '@core/help-articles-legacy';
import { HelpArticlesDirective } from '@core/help-articles-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  EmptyStateComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayPosition,
  Select2Component,
  Select2TriggerDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { ViewFilterDef, ViewFilterInputDef, FilterControl } from '@core/views-types';
import { FilterGroupOperator } from '@core/views-types';
import { dataSource$ } from '@core/views-util';

import { ViewsAnalytics } from '../../analytics/views-analytics';
import { FiltersDataService } from '../services/filters-data.service';
import { FiltersFormDataService } from '../services/filters-form-data.service';
import { FiltersFormStateService } from '../services/filters-form-state.service';
import { FiltersFormService } from '../services/filters-form.service';
import { FilterSelectComponent } from './filter/filter-select/filter-select.component';
import { FilterComponent } from './filter/filter.component';

interface FilterData {
  filterDef: ViewFilterDef;
  filterControl: FilterControl;
  filterInputDefs: ViewFilterInputDef[];
  filterState: unknown[];
  filterUuid: string;
}

@Component({
  selector: 'views-filters-container',
  imports: [
    CommonModule,
    HeadingComponent,
    PushPipe,
    FilterComponent,
    ReactiveFormsModule,
    IconButtonComponent,
    ButtonComponent,
    EmptyStateComponent,
    IconComponent,
    Select2Component,
    HelpArticlesDirective,
    Select2TriggerDirective,
    ButtonDropdownIndicatorDirective,
    TranslatePipe,
    FilterSelectComponent,
  ],
  templateUrl: './filters-container.component.html',
  styleUrls: ['./filters-container.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.rt-card]': 'true',
  },
  providers: [],
})
export class FiltersContainerComponent implements OnInit {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly filtersFormService = inject(FiltersFormService);
  private readonly filterFormStateService = inject(FiltersFormStateService);
  private readonly filtersFormDataService = inject(FiltersFormDataService);
  private readonly filtersDataService = inject(FiltersDataService);
  private readonly i18n = inject(I18nService);
  private readonly viewsAnalytics = inject(ViewsAnalytics);

  public readonly helpArticleLink = <HelpTranslationLinkMapKey>'views';

  @Input() public viewsRecordsTotalCount: number | null;

  public close = output<void>();

  public OverlayPosition = OverlayPosition;
  public dataSourceId$ = inject(dataSource$).pipe(map(({ id }) => id));
  public filterForm = this.filterFormStateService.filtersForm;
  public defaultFilterDef: ViewFilterDef;
  public filterOperators = {
    [FilterGroupOperator.And]: this.i18n.translate('app.views.filters.operator_and'),
    [FilterGroupOperator.Or]: this.i18n.translate('app.views.filters.operator_or'),
  };
  public filterOperatorsItems = Object.entries(this.filterOperators).map(([value, label]) => ({
    label,
    value,
  }));
  public filterData: FilterData[] = [];

  private untilDestroyed = injectUntilDestroyed();

  public ngOnInit() {
    this.watchAdvancedFiltersData();
    this.watchDefaultFilterDef();
    this.formDetectChanges();
  }

  public onFilterRemove(filter: FilterControl) {
    this.filterForm.markAsDirty();
    this.viewsAnalytics.removeFilter({ filterId: filter.value.id! });
    this.filtersFormService.removeFilter(filter);
    this.cdRef.detectChanges();
  }

  public onFilterStateChanges(uuid: string, state: unknown[]) {
    this.filterFormStateService.updateFilterState(uuid, state);
  }

  public addFilter() {
    this.filterForm.markAsDirty();
    this.filtersFormService.addFilter(this.defaultFilterDef);
  }

  public clearFilters() {
    this.filterForm.markAsDirty();
    this.filtersFormService.clearForm();
    this.close.emit();
  }

  public onFilterSelect(filterId: string) {
    this.filterForm.markAsDirty();
    this.filtersFormService.addFilter(this.filtersDataService.filterDefMap.get(filterId)!);
    this.viewsAnalytics.addFilter({ filterId });
  }

  public onOperatorSelect(event: Select2Item<string>) {
    this.viewsAnalytics.changeFiltersOperator({ operator: event.value });
  }

  private watchAdvancedFiltersData() {
    this.filtersFormDataService.advancedFiltersData$.pipe(this.untilDestroyed).subscribe(data => {
      this.filterData = data;
      this.cdRef.detectChanges();
    });
  }

  private watchDefaultFilterDef() {
    this.filtersDataService.dataSourceFilterDefMap$
      .pipe(this.untilDestroyed)
      .subscribe(filterDefMap => {
        this.defaultFilterDef = <ViewFilterDef>(
          Object.values(filterDefMap).find(filter => filter.type === 'filter')!
        );
        this.cdRef.detectChanges();
      });
  }

  private formDetectChanges() {
    merge(this.filterForm.valueChanges, this.filterFormStateService.formChanged$)
      .pipe(this.untilDestroyed)
      .subscribe(() => this.cdRef.detectChanges());
  }
}
